<template>
    <section class="relative overflow-hidden">
        <div class="absolute inset-0 -z-10 bg-white" />
        <div class="absolute -top-32 -right-20 -z-10 h-[520px] w-[520px] rounded-full bg-brand-blue-8 blur-3xl" />
        <div class="absolute top-40 -left-32 -z-10 h-[480px] w-[480px] rounded-full bg-brand-cyan-8 blur-3xl" />
        <div class="absolute bottom-0 left-1/3 -z-10 h-[360px] w-[360px] rounded-full bg-brand-green-6 blur-3xl" />
        <div class="absolute inset-x-0 bottom-0 -z-10 h-32 bg-gradient-to-b from-transparent to-[var(--color-soft-bg)]" />

        <div class="mx-auto max-w-7xl px-5 sm:px-8 pt-10 md:pt-16 pb-20 md:pb-28 grid lg:grid-cols-12 gap-10 lg:gap-12 items-center">
            <div class="order-1 lg:col-span-7">
                <div class="inline-flex items-center gap-2 rounded-full border border-brand-green-25 bg-white-80 backdrop-blur px-3.5 py-1.5 text-xs md:text-sm font-bold text-brand-navy-85 shadow-sm" :style="{ animation: 'fade-up 0.6s ease-out both' }">
                    <span class="relative flex h-2 w-2">
                        <span class="absolute inline-flex h-full w-full rounded-full bg-brand-green opacity-75 animate-ping" />
                        <span class="relative inline-flex h-2 w-2 rounded-full bg-brand-green" />
                    </span>
                    الترم الجديد بدأ — سجّل دلوقتي
                </div>

                <h1 class="seo-sr-only">البرمجة مع غنيم</h1>
                <h2 class="mt-5 text-4xl sm:text-5xl lg:text-[64px] font-extrabold leading-[1.15] text-brand-navy" :style="{ animation: 'fade-up 0.7s 0.08s ease-out both' }">
                    افهم
                    <span
                        class="bg-clip-text text-transparent"
                        :style="{
                            backgroundImage: 'linear-gradient(90deg, #E63946, #F26B5A, #22C55E, #E63946)',
                            backgroundSize: '200% 100%',
                            animation: 'gradient-shimmer 5s ease-in-out infinite',
                        }"
                    >
                        Programming & AI
                    </span>
                    <br />
                    واكتب الكود
                    <span class="relative inline-block">
                        <span class="relative z-10">بإيدك</span>
                        <svg class="absolute -bottom-2 left-0 w-full" height="14" viewBox="0 0 200 14" fill="none" preserveAspectRatio="none">
                            <path d="M2 9 C 50 2, 150 2, 198 9" stroke="#22C55E" stroke-width="4" stroke-linecap="round" fill="none" opacity="0.7" />
                        </svg>
                    </span>
                </h2>

                <p class="mt-6 text-base md:text-lg text-brand-navy-70 leading-relaxed max-w-xl" :style="{ animation: 'fade-up 0.7s 0.16s ease-out both' }">
                    شرح، تدريب عملي، ومراجعات تساعدك تفهم البرمجة والذكاء الاصطناعي وتدخل الامتحان بثقة.
                </p>

                <div class="mt-7 flex flex-col sm:flex-row flex-wrap gap-3" :style="{ animation: 'fade-up 0.7s 0.24s ease-out both' }">
                    <router-link
                        :to="{ name: 'register' }"
                        class="group relative inline-flex items-center justify-center gap-2 rounded-full px-8 py-4 text-base font-extrabold text-white shadow-[var(--shadow-glow)] hover:shadow-[0_25px_70px_-15px_rgba(37,99,235,0.7)] transition-all hover:scale-[1.03] overflow-hidden"
                        :style="{ background: 'linear-gradient(135deg, #E63946 0%, #B71C2A 50%, #F26B5A 100%)' }"
                    >
                        <span class="absolute inset-0 -translate-x-full bg-gradient-to-r from-transparent via-white-25 to-transparent group-hover:translate-x-full transition-transform duration-700" />
                        <span class="relative z-10">ابدأ الآن مجاناً</span>
                        <lucide-icon name="ArrowLeft" class="relative z-10 h-5 w-5 transition-transform group-hover:-translate-x-1" />
                    </router-link>
                    <a
                        :href="YOUTUBE_URL"
                        target="_blank"
                        rel="noopener noreferrer"
                        class="group inline-flex items-center justify-center gap-2.5 rounded-full px-7 py-4 text-base font-extrabold text-white shadow-[0_15px_40px_-12px_rgba(255,0,0,0.5)] hover:shadow-[0_20px_50px_-12px_rgba(255,0,0,0.7)] transition-all hover:scale-[1.03]"
                        :style="{ background: 'linear-gradient(135deg, #FF0000 0%, #CC0000 100%)' }"
                    >
                        <span class="inline-flex h-7 w-10 items-center justify-center rounded-md bg-white-95">
                            <svg viewBox="0 0 24 24" class="h-4 w-4 fill-[#FF0000]"><path d="M8 5v14l11-7z" /></svg>
                        </span>
                        شوف قناة اليوتيوب
                    </a>
                </div>

                <div class="mt-4 flex flex-wrap items-center gap-x-5 gap-y-2 text-xs md:text-sm text-brand-navy-60" :style="{ animation: 'fade-up 0.7s 0.3s ease-out both' }">
                    <span class="inline-flex items-center gap-1.5"><lucide-icon name="CheckCircle2" class="h-4 w-4 text-brand-green" /> مجاني تجرّب</span>
                    <span class="inline-flex items-center gap-1.5"><lucide-icon name="CheckCircle2" class="h-4 w-4 text-brand-green" /> بدون بطاقة</span>
                    <span class="inline-flex items-center gap-1.5"><lucide-icon name="CheckCircle2" class="h-4 w-4 text-brand-green" /> شرح للغات والعربي</span>
                </div>

                <div dir="rtl" class="mt-5 flex flex-wrap items-center gap-2" :style="{ animation: 'fade-up 0.7s 0.34s ease-out both' }">
                    <a href="#courses" class="inline-flex items-center rounded-full border border-brand-blue-25 bg-brand-blue-5 px-4 py-1.5 text-xs md:text-sm font-bold text-brand-blue hover:bg-brand-blue hover:text-white transition-colors">
                        أولى ثانوي
                    </a>
                    <a href="#courses" class="inline-flex items-center rounded-full border border-brand-cyan-30 bg-brand-cyan-5 px-4 py-1.5 text-xs md:text-sm font-bold text-brand-cyan hover:bg-brand-cyan hover:text-white transition-colors">
                        تانية ثانوي
                    </a>
                    <a href="#courses" class="inline-flex items-center rounded-full border border-brand-navy-15 bg-white px-4 py-1.5 text-xs md:text-sm font-bold text-brand-navy-80 hover:bg-brand-navy hover:text-white transition-colors">
                        كورسات إضافية
                    </a>
                </div>

                <div class="mt-8 flex flex-wrap items-center gap-x-6 gap-y-4 pt-6 border-t border-brand-navy-8" :style="{ animation: 'fade-up 0.7s 0.38s ease-out both' }">
                    <div class="flex items-center gap-3">
                        <div class="flex -space-x-2 rtl:space-x-reverse rtl:-space-x-reverse">
                            <div
                                v-for="(g, i) in avatarGradients"
                                :key="i"
                                :class="`h-9 w-9 rounded-full border-2 border-white bg-gradient-to-br ${g} flex items-center justify-center text-white text-xs font-bold shadow-sm`"
                            >
                                {{ avatarLetters[i] }}
                            </div>
                        </div>
                        <div class="text-sm">
                            <div class="font-extrabold text-brand-navy leading-tight">+10000 طالب</div>
                            <div class="text-xs text-brand-navy-55">بيتعلموا مع غنيم</div>
                        </div>
                    </div>

                    <div class="hidden sm:block h-10 w-px bg-brand-navy-10" />

                    <div class="text-sm">
                        <div class="flex items-center gap-1 text-amber-500">
                            <svg v-for="i in 5" :key="i" viewBox="0 0 24 24" class="h-4 w-4 fill-current"><path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" /></svg>
                        </div>
                        <div class="text-xs text-brand-navy-55 mt-0.5"><span class="font-bold text-brand-navy">4.9/5</span> تقييم الطلاب</div>
                    </div>

                    <div class="hidden md:block h-10 w-px bg-brand-navy-10" />

                    <div class="text-xs text-brand-navy-55">
                        <div class="mb-1">جزء من منظومة</div>
                        <div class="flex items-center gap-3 font-bold text-brand-navy-75">
                            <span>YallaStep</span>
                            <span class="text-brand-navy-20">·</span>
                            <span>DerasaTech</span>
                            <span class="text-brand-navy-20">·</span>
                            <span>eijaaz</span>
                        </div>
                    </div>
                </div>
            </div>

            <div class="order-2 lg:col-span-5 relative h-[420px] sm:h-[500px] lg:h-[600px]" :style="{ animation: 'fade-up 0.9s 0.2s ease-out both' }">
                <div class="absolute inset-0 flex items-end justify-center">
                    <img
                        :src="heroImage"
                        alt="محمد غنيم — مدرس البرمجة والذكاء الاصطناعي"
                        width="1024"
                        height="1024"
                        loading="eager"
                        decoding="async"
                        class="relative z-10 h-full w-auto max-w-full object-contain drop-shadow-[0_30px_50px_rgba(37,99,235,0.25)]"
                        :style="{ animation: 'float 7s ease-in-out infinite' }"
                    />
                </div>

                <div class="absolute top-2 right-2 sm:right-4 z-20 flex gap-2" :style="{ animation: 'fade-up 0.7s 0.5s ease-out both' }">
                    <span class="rounded-full bg-white border border-brand-blue-20 px-3 py-1.5 text-xs font-bold text-brand-blue shadow-sm">Grade 10</span>
                    <span class="rounded-full bg-brand-blue text-white px-3 py-1.5 text-xs font-bold shadow-[var(--shadow-glow)]">Grade 11</span>
                </div>

                <div class="absolute top-4 left-2 sm:left-4 z-20 bg-white rounded-2xl shadow-[var(--shadow-card)] px-4 py-2.5 flex items-center gap-2.5" :style="{ animation: 'float 6s ease-in-out infinite' }">
                    <span class="relative flex h-2.5 w-2.5">
                        <span class="absolute inline-flex h-full w-full rounded-full bg-red-500 opacity-75 animate-ping" />
                        <span class="relative inline-flex h-2.5 w-2.5 rounded-full bg-red-500" />
                    </span>
                    <div>
                        <div class="text-[10px] font-bold tracking-wider text-red-500">LIVE NOW</div>
                        <div class="text-xs font-bold text-brand-navy leading-tight">مراجعة Grade 11 · 18:00</div>
                    </div>
                </div>

                <div class="absolute top-1/2 -translate-y-1/2 right-0 z-20 text-white rounded-2xl shadow-[var(--shadow-glow)] px-4 py-3" :style="{ background: 'linear-gradient(135deg, #E63946, #F26B5A)', animation: 'drift 12s ease-in-out infinite' }">
                    <div class="flex items-center gap-2">
                        <span class="text-lg">🏆</span>
                        <div>
                            <div class="text-[10px] opacity-80 font-medium">الهدف</div>
                            <div class="text-lg font-extrabold leading-none">100/100</div>
                        </div>
                    </div>
                </div>

                <div class="absolute top-20 right-0 z-20 bg-white rounded-2xl shadow-[var(--shadow-card)] px-3 py-2 flex items-center gap-2 border border-brand-blue-10" :style="{ animation: 'float 5s ease-in-out infinite' }">
                    <span class="inline-flex h-7 w-7 items-center justify-center rounded-lg bg-yellow-400 text-[10px] font-extrabold text-brand-navy">JS</span>
                    <span class="text-xs font-bold text-brand-navy">JavaScript</span>
                </div>

                <div class="absolute top-1/3 left-0 z-20 bg-white rounded-xl shadow-[var(--shadow-card)] px-3 py-2 flex items-center gap-2 border border-brand-cyan-15" :style="{ animation: 'drift 10s ease-in-out infinite' }">
                    <lucide-icon name="Brain" class="h-4 w-4 text-brand-cyan" />
                    <span class="text-xs font-bold text-brand-cyan">AI</span>
                </div>

                <div class="absolute bottom-40 right-2 z-20 bg-white rounded-xl shadow-[var(--shadow-card)] px-3 py-2 flex items-center gap-2 border border-brand-green-15" :style="{ animation: 'float 7s ease-in-out infinite' }">
                    <lucide-icon name="Code2" class="h-4 w-4 text-brand-green" />
                    <span class="text-xs font-bold text-brand-navy font-mono">&lt;/&gt;</span>
                </div>

                <div class="hidden sm:flex absolute bottom-24 right-1/3 z-20 bg-brand-navy text-white rounded-xl shadow-[var(--shadow-card)] px-3 py-2 items-center gap-2" :style="{ animation: 'drift 14s ease-in-out infinite' }">
                    <span class="font-mono text-[11px] font-bold text-emerald-300">{ }</span>
                    <span class="text-[11px] font-bold">Coding Lab</span>
                </div>

                <div class="absolute bottom-1/3 left-1/3 z-10" :style="{ animation: 'spin-slow 22s linear infinite reverse' }">
                    <lucide-icon name="Sparkles" class="h-4 w-4 text-brand-green" />
                </div>

                <div class="hidden md:block absolute bottom-0 left-0 z-20 w-[280px] rounded-2xl bg-[#1A0E10] shadow-[0_25px_60px_-15px_rgba(11,18,32,0.45)] overflow-hidden border border-white-10" :style="{ animation: 'float 8s ease-in-out infinite', direction: 'ltr' }">
                    <div class="flex items-center justify-between px-3 py-2 bg-white-5 border-b border-white-5">
                        <div class="flex gap-1.5">
                            <span class="h-2.5 w-2.5 rounded-full bg-red-400" />
                            <span class="h-2.5 w-2.5 rounded-full bg-amber-400" />
                            <span class="h-2.5 w-2.5 rounded-full bg-emerald-400" />
                        </div>
                        <div class="text-[10px] font-mono text-white-50">main.js</div>
                        <lucide-icon name="Code2" class="h-3.5 w-3.5 text-white-40" />
                    </div>
                    <pre class="px-4 py-3 text-[11px] leading-relaxed font-mono text-white-90"><span class="text-pink-400">const</span> <span class="text-cyan-300">learn</span> = <span class="text-pink-400">() =&gt;</span> {<br>  <span class="text-pink-400">const</span> <span class="text-purple-300">ai</span> = <span class="text-emerald-300">"الذكاء الاصطناعي"</span>;<br>  <span class="text-pink-400">return</span> <span class="text-emerald-300">`أنا فاهم ${ai}`</span>;<br>};<br><span class="text-cyan-300">console</span>.<span class="text-amber-300">log</span>(<span class="text-cyan-300">learn</span>())<span class="inline-block w-2 h-3 bg-cyan-300 align-middle ml-0.5" :style="{ animation: 'blink 1s steps(1) infinite' }" /></pre>
                </div>
            </div>
        </div>
    </section>
</template>

<script>
import LucideIcon from './LucideIcon.vue';
import { YOUTUBE_URL } from './landing-data';

export default {
    name: 'WelcomeHero',
    components: { LucideIcon },
    data() {
        return {
            heroImage: require('@/assets/welcome/ghonaim-hero.webp'),
            YOUTUBE_URL,
            avatarGradients: [
                'from-brand-blue to-brand-cyan',
                'from-brand-cyan to-brand-green',
                'from-brand-green to-brand-blue',
                'from-brand-blue to-brand-navy',
            ],
            avatarLetters: ['م', 'أ', 'س', 'ن'],
        };
    },
};
</script>
